import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import { FeatureCollection, Feature } from 'geojson';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { theme } from '../../styles/theme';
import { ChevronDown, Info, Globe, TrendingUp, TrendingDown, Search, Map } from 'lucide-react';
import administrationApi, { Country } from '../../api/administrationApi';
import metricsService, { CountryMetricValuesResponse } from '../../services/metricsService';
import D3EqualEarthMap from './D3EqualEarthMap';
import ResetViewControl from './ResetViewControl';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

declare global {
  interface Window {
    loggedFeatures?: boolean;
  }
}

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface GlobalImpactMapProps {
  darkMode: boolean;
}

interface CountryMetricData {
  countryCode: string;
  countryName: string;
  value: number;
}

interface MetricOption {
  id: string;
  metricId: string;
  name: string;
  description: string;
  unit: string;
  cluster?: string;
}

const GlobalImpactMap: React.FC<GlobalImpactMapProps> = ({ darkMode }) => {
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [countryData, setCountryData] = useState<CountryMetricData[]>([]);
  const [loading, setLoading] = useState(false);
  const [geoJsonData, setGeoJsonData] = useState<FeatureCollection | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [metricOptions, setMetricOptions] = useState<MetricOption[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedScenarioType, setSelectedScenarioType] = useState<'baseline' | 'optimistic' | 'pessimistic'>('baseline');
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const [metricSearchTerm, setMetricSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mapProjection, setMapProjection] = useState<'mercator' | 'natural' | 'equal'>('equal');

  // Map projection configurations
  const projectionConfigs = {
    mercator: {
      name: 'Web Mercator',
      center: [20, 0] as [number, number],
      zoom: 2,
      url: darkMode 
        ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      type: 'leaflet' as const,
    },
    natural: {
      name: 'Natural Earth',
      center: [20, 0] as [number, number],
      zoom: 2,
      url: darkMode
        ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        : "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      type: 'leaflet' as const,
    },
    equal: {
      name: 'Equal Earth',
      type: 'd3' as const,
    },
  };

  // Helper function to determine metric type from unit
  const getMetricType = (unit: string): 'percentage' | 'currency' | 'count' => {
    if (!unit) return 'count';
    const lowerUnit = unit.toLowerCase();
    if (lowerUnit.includes('percent') || lowerUnit === '%') return 'percentage';
    if (lowerUnit.includes('currency') || lowerUnit.includes('dollar') || lowerUnit.includes('eur') || lowerUnit === '$' || lowerUnit === '€') return 'currency';
    return 'count';
  };

  // Helper function to format values based on metric type
  const formatValue = (value: number, unit?: string): string => {
    const metricType = getMetricType(unit || '');
    
    switch (metricType) {
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'currency':
        // Format as currency with thousand separators and 2 decimal places
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'count':
      default:
        // Format as number with thousand separators
        if (Math.abs(value) >= 1000) {
          return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
        }
        return value.toFixed(2);
    }
  };

  // Get the current selected metric's unit
  const getCurrentMetricUnit = () => {
    const metric = metricOptions.find(m => m.metricId === selectedMetric);
    return metric?.unit || '';
  };

  // Load GeoJSON data for world countries
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
      .then(response => response.json())
      .then(data => setGeoJsonData(data))
      .catch(error => console.error('Error loading GeoJSON:', error));
  }, []);

  // Load countries and metrics
  useEffect(() => {
    loadCountries();
    loadAvailableMetrics();
  }, []);

  const loadCountries = async () => {
    try {
      const response = await administrationApi.getCountries({ limit: 1000 });
      setCountries(response.countries);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadAvailableMetrics = async () => {
    try {
      console.log('Loading metrics from API...');
      // Fetch real metrics from the API
      const response = await metricsService.getMetrics({ limit: 100, page: 1 });
      console.log('Metrics API response:', response);
      
      if (response && Array.isArray(response)) {
        const metricsData = response;
        console.log('Metrics data:', metricsData);
        
        const metrics: MetricOption[] = metricsData.map((metric: any) => ({
          id: metric.id.toString(),
          metricId: metric.metricId,
          name: metric.name,
          description: metric.definition || '',
          unit: metric.unit || '',
          cluster: metric.metricId ? metric.metricId.split('.')[0] : undefined
        }));
        
        console.log(`Loaded ${metrics.length} metrics from API`);
        setMetricOptions(metrics);
        
        // Select the first metric by default if available
        if (metrics.length > 0) {
          setSelectedMetric(metrics[0].metricId);
        }
      } else {
        throw new Error('No metrics data available');
      }
    } catch (error) {
      console.error('Failed to load metrics from API:', error);
      // Don't fall back to sample metrics - show the actual error
      setMetricOptions([]);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMetricDropdown(false);
        setMetricSearchTerm('');
      }
    };

    if (showMetricDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMetricDropdown]);

  // Load metric data for all countries when a metric is selected
  useEffect(() => {
    if (selectedMetric && countries.length > 0) {
      loadMetricData();
    }
  }, [selectedMetric, countries, selectedScenarioType]);

  const loadMetricData = async () => {
    setLoading(true);
    try {
      console.log(`Loading metric data for ${selectedMetric} with scenario ${selectedScenarioType}`);
      // Fetch metric values for all countries from the API
      const response = await metricsService.getAllCountryMetricValues(
        selectedMetric,
        undefined, // year - will get latest
        selectedScenarioType
      );
      
      console.log('Metric values response:', response);
      
      // Process the data to create country metric data
      const processedData = processCountryMetricData(response);
      console.log(`Processed ${processedData.length} country data points`);
      setCountryData(processedData);
    } catch (error) {
      console.error('Failed to load metric data, generating sample:', error);
      // Generate sample data with known countries from the database
      generateSampleData();
    } finally {
      setLoading(false);
    }
  };

  const processCountryMetricData = (response: CountryMetricValuesResponse): CountryMetricData[] => {
    if (!response || !response.countries) {
      return [];
    }
    
    const processed = response.countries.map(country => ({
      countryCode: country.countryCode,
      countryName: country.countryName,
      value: country.value,
      // Remove percentageChange as we don't have this data
    }));
    
    // Debug: log China's data specifically
    const chinaData = processed.find(c => 
      c.countryCode === 'CHN' || 
      c.countryName?.toLowerCase().includes('china')
    );
    if (chinaData) {
      console.log('China data from API:', chinaData);
    }
    
    return processed;
  };
  
  const generateSampleData = () => {
    // Generate sample data for demonstration if API fails
    if (countries.length > 0) {
      const sampleData = countries.map(country => ({
        countryCode: country.iso_code,
        countryName: country.name,
        value: Math.random() * 100,
      }));
      setCountryData(sampleData);
    }
  };

  // Calculate color scale based on metric values using quantile binning
  const { minValue, maxValue, colorScale, quantileThresholds } = useMemo(() => {
    if (countryData.length === 0) {
      return { minValue: 0, maxValue: 100, colorScale: () => '#e0e0e0', quantileThresholds: [] };
    }
    
    const values = countryData.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Define the 6 colors for quantile bins
    const quantileColors = [
      '#F8F6FB', // Ghost White
      '#E6E0F2', // Whisper Lavender
      '#CFC5E3', // Soft Lavender
      '#B1A3CC', // Medium Purple
      '#8E7DAD', // Purple
      '#5D4E7D', // Dark Purple
    ];
    
    // Sort values for quantile calculation
    const sortedValues = [...values].sort((a, b) => a - b);
    
    // Calculate quantile thresholds
    const thresholds: number[] = [];
    for (let i = 1; i < 6; i++) {
      const index = Math.floor((sortedValues.length * i) / 6);
      thresholds.push(sortedValues[index]);
    }
    
    const scale = (value: number) => {
      // Find which quantile bin this value falls into
      let binIndex = 0;
      for (let i = 0; i < thresholds.length; i++) {
        if (value > thresholds[i]) {
          binIndex = i + 1;
        }
      }
      return quantileColors[binIndex];
    };
    
    return { minValue: min, maxValue: max, colorScale: scale, quantileThresholds: thresholds };
  }, [countryData]);

  // Style function for GeoJSON features
  const styleFeature = (feature?: Feature): L.PathOptions => {
    if (!feature) {
      return {
        fillColor: '#e0e0e0',
        weight: 1,
        opacity: 1,
        color: darkMode ? '#444' : '#fff',
        fillOpacity: 0.7,
      };
    }
    
    // Try multiple properties to match country codes
    // GeoJSON uses ISO3166-1-Alpha-3 for 3-letter codes, ISO3166-1-Alpha-2 for 2-letter codes
    const countryCode = feature.properties?.['ISO3166-1-Alpha-3'] || feature.properties?.['ISO3166-1-Alpha-2'];
    const countryName = feature.properties?.name;
    
    // Log first few features to debug
    if (countryData.length > 0 && !window.loggedFeatures) {
      console.log('GeoJSON feature properties:', feature.properties);
      console.log('Looking for country data with codes:', countryData.slice(0, 3));
      window.loggedFeatures = true;
    }
    
    // Match using ISO-3 code (database returns 3-letter codes)
    const data = countryData.find(d => 
      d.countryCode === feature.properties?.['ISO3166-1-Alpha-3'] ||
      d.countryName?.toLowerCase() === countryName?.toLowerCase()
    );
    
    const fillColor = data ? colorScale(data.value) : '#e0e0e0';
    
    return {
      fillColor,
      weight: hoveredCountry === countryCode ? 2 : 1,
      opacity: 1,
      color: darkMode ? '#444' : '#fff',
      fillOpacity: hoveredCountry === countryCode ? 0.9 : 0.7,
    };
  };

  // Event handlers for GeoJSON features
  const onEachFeature = (feature: Feature, layer: L.Layer) => {
    const countryCode = feature.properties?.['ISO3166-1-Alpha-3'];
    const countryName = feature.properties?.name;
    
    if (layer instanceof L.Path) {
      layer.on({
        mouseover: () => {
          setHoveredCountry(countryCode);
          // Dynamically find the data when hovering, not when creating the layer
          const data = countryData.find(d => 
            d.countryCode === countryCode ||
            d.countryName?.toLowerCase() === countryName?.toLowerCase()
          );
          
          if (data) {
            const unit = getCurrentMetricUnit();
            const formattedValue = formatValue(data.value, unit);
            // Debug: log the actual value
            console.log(`${countryName}: ISO3=${countryCode}, raw value = ${data.value}, formatted = ${formattedValue}`);
            layer.bindTooltip(
              `<div style="padding: 8px;">
                <strong>${countryName}</strong><br/>
                ${selectedMetric ? `Value: ${formattedValue}` : 'No metric selected'}
              </div>`,
              { sticky: true }
            ).openTooltip();
          } else {
            // Show tooltip even if no data
            console.log(`${countryName}: ISO3=${countryCode}, No data found in countryData`);
            layer.bindTooltip(
              `<div style="padding: 8px;">
                <strong>${countryName}</strong><br/>
                No data available
              </div>`,
              { sticky: true }
            ).openTooltip();
          }
        },
        mouseout: () => {
          setHoveredCountry(null);
          layer.unbindTooltip();
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Metric Selector */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowMetricDropdown(!showMetricDropdown)}
              className="px-4 py-2 rounded-lg flex items-center space-x-2 min-w-[250px]"
              style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
              }}
            >
              <Globe className="w-4 h-4" style={{ color: theme.colors.primary.main }} />
              <span className="flex-1 text-left">
                {selectedMetric ? metricOptions.find(m => m.metricId === selectedMetric)?.name : 'Select a metric...'}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showMetricDropdown && (
              <div
                className="absolute mt-2 w-[400px] rounded-lg shadow-lg"
                style={{
                  backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
                  border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  zIndex: 1000,
                  maxHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Search Input */}
                <div className="p-3 border-b" style={{
                  borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                }}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" 
                      style={{ color: theme.colors.text.secondary }} 
                    />
                    <input
                      type="text"
                      placeholder="Search metrics..."
                      value={metricSearchTerm}
                      onChange={(e) => setMetricSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 rounded-md"
                      style={{
                        backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                
                {/* Scrollable Metrics List */}
                <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
                  {metricOptions
                    .filter(metric => 
                      metricSearchTerm === '' || 
                      metric.metricId.toLowerCase().includes(metricSearchTerm.toLowerCase()) ||
                      metric.name.toLowerCase().includes(metricSearchTerm.toLowerCase())
                    )
                    .map(metric => (
                      <button
                        key={metric.metricId}
                        onClick={() => {
                          setSelectedMetric(metric.metricId);
                          setShowMetricDropdown(false);
                          setMetricSearchTerm('');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-black hover:bg-opacity-5 transition-colors flex items-center"
                        style={{
                          borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        }}
                      >
                        <span className="font-medium mr-2" style={{
                          color: theme.colors.primary.main,
                          minWidth: '60px',
                        }}>
                          {metric.metricId}
                        </span>
                        <span style={{
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                        }}>
                          {metric.name}
                        </span>
                      </button>
                    ))}
                  {metricOptions.filter(metric => 
                    metricSearchTerm === '' || 
                    metric.metricId.toLowerCase().includes(metricSearchTerm.toLowerCase()) ||
                    metric.name.toLowerCase().includes(metricSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="p-4 text-center" style={{
                      color: theme.colors.text.secondary,
                    }}>
                      No metrics found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Scenario Type Selector */}
          <div className="flex space-x-2">
            {(['pessimistic', 'baseline', 'optimistic'] as const).map(type => (
              <button
                key={type}
                onClick={() => setSelectedScenarioType(type)}
                className="px-3 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                style={{
                  backgroundColor: selectedScenarioType === type 
                    ? theme.colors.primary.main 
                    : darkMode ? theme.colors.background.dark : theme.colors.background.default,
                  color: selectedScenarioType === type 
                    ? '#fff' 
                    : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                  border: `1px solid ${selectedScenarioType === type 
                    ? theme.colors.primary.main 
                    : darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                }}
              >
                {type === 'pessimistic' && <TrendingDown className="w-3 h-3" />}
                {type === 'optimistic' && <TrendingUp className="w-3 h-3" />}
                <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </button>
            ))}
          </div>
          
          {/* Map Projection Selector */}
          <div className="flex items-center space-x-2">
            <Map className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
            <div className="flex space-x-1">
              {(Object.keys(projectionConfigs) as Array<keyof typeof projectionConfigs>).map(projection => (
                <button
                  key={projection}
                  onClick={() => setMapProjection(projection)}
                  className="px-2 py-1 rounded text-xs transition-colors"
                  style={{
                    backgroundColor: mapProjection === projection 
                      ? theme.colors.primary.main 
                      : darkMode ? theme.colors.background.dark : theme.colors.background.default,
                    color: mapProjection === projection 
                      ? '#fff' 
                      : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                    border: `1px solid ${mapProjection === projection 
                      ? theme.colors.primary.main 
                      : darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  }}
                  title={projectionConfigs[projection].name}
                >
                  {projectionConfigs[projection].name}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Info */}
        <div className="flex items-center space-x-2">
          <Info className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
          <span className="text-sm" style={{ color: theme.colors.text.secondary }}>
            {loading ? 'Loading data...' : 'Hover over countries to see details'}
          </span>
        </div>
      </div>
      
      {/* Map Container */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          height: '500px',
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
        }}
      >
        {projectionConfigs[mapProjection].type === 'leaflet' ? (
          <MapContainer
            center={(projectionConfigs[mapProjection] as any).center}
            zoom={(projectionConfigs[mapProjection] as any).zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            minZoom={1}
            maxZoom={6}
            attributionControl={false}
            key={mapProjection} // Force re-render when projection changes
          >
            <TileLayer
              url={(projectionConfigs[mapProjection] as any).url}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            {geoJsonData && (
              <GeoJSON
                data={geoJsonData}
                style={styleFeature}
                onEachFeature={onEachFeature}
                key={`${selectedMetric}-${selectedScenarioType}-${countryData.length}-${mapProjection}`}
              />
            )}
            <ResetViewControl 
              center={(projectionConfigs[mapProjection] as any).center}
              zoom={(projectionConfigs[mapProjection] as any).zoom}
              darkMode={darkMode}
              position="topleft"
            />
          </MapContainer>
        ) : (
          <D3EqualEarthMap
            darkMode={darkMode}
            countryData={countryData}
            colorScale={colorScale}
            formatValue={formatValue}
            getCurrentMetricUnit={getCurrentMetricUnit}
            selectedMetric={selectedMetric}
          />
        )}
      </div>
      
      {/* Color Scale Legend */}
      {selectedMetric && countryData.length > 0 && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
            }}>
              {metricOptions.find(m => m.metricId === selectedMetric)?.name} Scale
            </span>
            <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
              {countryData.length} countries with data
            </span>
          </div>
          
          <div className="flex flex-col space-y-2">
            {/* Display quantile bins */}
            <div className="flex items-center space-x-2">
              {['#F8F6FB', '#E6E0F2', '#CFC5E3', '#B1A3CC', '#8E7DAD', '#5D4E7D'].map((color, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full h-6 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                    {index === 0 ? `≤${formatValue(quantileThresholds[0] || minValue, getCurrentMetricUnit())}` :
                     index === 5 ? `>${formatValue(quantileThresholds[4] || maxValue, getCurrentMetricUnit())}` :
                     `≤${formatValue(quantileThresholds[index] || 0, getCurrentMetricUnit())}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="text-xs text-center" style={{ color: theme.colors.text.secondary }}>
              Quantile bins (each contains ~{Math.round(100/6)}% of countries)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalImpactMap;